import React, { forwardRef, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Chip,
  Link,
  Palette,
  SxProps,
  Typography,
  useTheme
} from '@mui/material';
import DataTable, {
  TableProps,
  TableColumn,
  PaginationComponentProps
} from 'react-data-table-component';
import SwapVertIcon from '@mui/icons-material/SwapVert';
import { Checkbox } from 'components/Checkbox/Checkbox';
import { getVisibleScrollbarStyles } from './visibleScrollbarStyles';
import { FilterOption } from './types';
import { FilterButton } from './FilterButton';
import { Loading } from 'components/Loading';
import { TablePagination } from './TablePagination';
import {
  DoNotDisturb as DoNotDisturbIcon,
  InfoOutlined as InfoOutlinedIcon
} from '@mui/icons-material';
import { useSearchParams } from 'react-router-dom';
import {
  deleteBulkSearchParams,
  deleteSearchParams
} from 'services/navigation/paths';

interface TableV2Column<T> extends TableColumn<T> {
  filterable?: boolean;
  filterOptions?: FilterOption[];
  filterId?: string;
  // Replaces the built-in single-select FilterButton. The column owns the
  // control's state; the table only places it in the header.
  filterComponent?: React.ReactNode;
  isAction?: boolean;
  minWidth?: string;
}

// NOTE: Omitted selectableRowsComponent from TableProps due to custom implementation

interface ExtendedTableProps<T> extends TableProps<T> {
  isLoading?: boolean;
  serverPage?: number;
  headStyles?: [
    {
      id: string;
      backgroundColor?: string;
      color?: string;
    }
  ];
  columns: TableV2Column<T>[];
  onFilterSelect?: ({
    value,
    column,
    filterId
  }: {
    value: string | boolean;
    column: string;
    filterId: string;
  }) => void;
  isFiltering?: boolean;
  onRemoveFilters?: (filterId: string) => void;
  onRemoveAllFilters?: () => void;
  hideRemoveFilters?: boolean;
  handleNext?: () => void;
  handlePrevious?: () => void;
  sortField?: string;
  emptyStateComponent?: React.ReactNode;
  alwaysShowScrollbar?: boolean;
  // Columns at their minimum widths fill at least this many pixels. Without
  // it, growing columns size the table to their content, not to the screen.
  minTableWidth?: number;
  filters?: {
    filterId: string;
    // Only read to tick the matching option inside a column's FilterButton.
    // Chips that own their own control (tags) carry no value.
    value?: string | boolean;
    label: string;
  }[];
}

interface CheckboxProps {
  disabled: boolean;
}

const filterContainer: SxProps = {
  display: 'flex',
  alignItems: 'center'
};

const actionContainer: SxProps = {
  display: 'flex',
  justifyContent: 'flex-end',
  alignItems: 'center',
  width: '100%'
};

const paginationContainer: SxProps = {
  display: 'flex',
  flex: 1,
  justifyContent: 'flex-end'
};

const inbetweenStateContainer: SxProps = {
  mt: 4,
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  padding: '60px'
};

const inbetweenStateTitle: SxProps = {
  fontSize: 20,
  fontWeight: 600
};

const inbetweenStateSubtitle: SxProps = {
  fontSize: 16,
  fontWeight: 400,
  mt: 1,
  color: 'warning.dark'
};

const noDataIconContainer: SxProps = {
  p: 2,
  display: 'flex',
  borderRadius: 9999
};

// Wrap rather than extend: without this the chips push the table wider than
// the viewport and the whole page scrolls sideways.
const filtersContainer: SxProps = {
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: 2,
  mb: 2
};

// Roughly one row of chips at a typical width. Past this the bar is collapsed
// behind a count, so a heavily filtered table does not lose half its height to
// the filters describing it.
const MAX_VISIBLE_FILTERS = 6;

const getCustomStyles = (
  palette: Palette,
  headStyles?: Array<{ id: string; backgroundColor?: string; color?: string }>,
  sortField?: string,
  alwaysShowScrollbar?: boolean,
  minTableWidth?: number
) => {
  const customHeadCellStyles = headStyles?.reduce((acc, headStyle) => {
    return {
      ...acc,
      [`&[data-column-id='${headStyle.id}']`]: {
        backgroundColor: headStyle.backgroundColor,
        color: headStyle.color
      }
    };
  }, {});

  return {
    responsiveWrapper: {
      style: alwaysShowScrollbar ? getVisibleScrollbarStyles(palette) : {}
    },
    tableWrapper: {
      style: minTableWidth
        ? { display: 'block', width: `max(100%, ${minTableWidth}px)` }
        : {}
    },
    table: {
      style: {
        '.rdt_TableHeadRow': {
          borderBottom: `1px solid ${palette.customGrey.light}`,
          backgroundColor: palette.customGrey.light
        }
      }
    },
    headCells: {
      style: {
        backgroundColor: palette.customGrey.light,
        padding: '0px 16px',
        color: palette.customGrey.dark,
        fontSize: '16px',
        fontWeight: 600,
        ...customHeadCellStyles,
        ...(sortField && {
          [`&[data-column-id='${sortField}']`]: {
            backgroundColor: palette.customGrey.medium
          }
        })
      }
    },
    cells: {
      style: {
        color: palette.customGrey.dark,
        fontSize: '16px',
        minHeight: '56px',
        ...(sortField && {
          [`&[data-column-id='${sortField}']`]: {
            backgroundColor: palette.customGrey.light
          }
        })
      }
    },
    rows: {
      style: {
        minHeight: '56px',
        fontSize: '16px',
        '&.rdt_TableRow': {
          borderBottom: `1px solid ${palette.customGrey.light}`
        }
      }
    }
  };
};

const CustomCheckbox = forwardRef<
  HTMLInputElement,
  CheckboxProps & { selectableRowsSingle: boolean }
>((props) => {
  if (props.disabled) {
    return null;
  }

  return <Checkbox isRadioStyle={props.selectableRowsSingle} {...props} />;
});

function TableV2<T>(props: ExtendedTableProps<T>) {
  const theme = useTheme();
  const {
    isLoading,
    columns,
    onFilterSelect,
    onRemoveFilters,
    onRemoveAllFilters,
    hideRemoveFilters,
    selectableRowDisabled,
    handlePrevious,
    handleNext,
    paginationTotalRows,
    serverPage,
    selectableRowsSingle,
    emptyStateComponent,
    filters
  } = props;

  const { palette } = theme;
  const [clientPage, setClientPage] = useState(1);
  const currentPage = serverPage ?? clientPage;
  const [resetPaginationToggle, setResetPaginationToggle] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [showAllFilters, setShowAllFilters] = useState(false);
  const isFiltering = !!filters?.length || props.isFiltering;

  const columnsFormatted = useMemo(() => {
    return columns.map((column) => {
      const { filterOptions, isAction, filterable, width, filterComponent } =
        column;

      if (isAction) {
        return {
          ...column,
          name: (
            <Box sx={actionContainer}>
              <span>{column.name}</span>
            </Box>
          ),
          style: { ...actionContainer }
        };
      }

      const filter = filters?.find(
        (filter) => filter.filterId === column.filterId
      );

      if (filterable) {
        return {
          ...column,
          name: (
            <Box sx={{ ...filterContainer, width }}>
              <span>{column.name}</span>
              {filterComponent ?? (
                <FilterButton
                  onFilterSelect={(value) =>
                    column.filterId &&
                    onFilterSelect?.({
                      value,
                      column: String(column.name),
                      filterId: column.filterId
                    })
                  }
                  filterOptions={filterOptions || []}
                  onRemoveFilters={() =>
                    onRemoveFilters?.(column.filterId as string)
                  }
                  hideRemoveFilters={hideRemoveFilters}
                  filterId={column.filterId}
                  filterValue={filter?.value}
                />
              )}
            </Box>
          )
        };
      }
      return column;
    });
  }, [columns, onFilterSelect, filters]);

  const handlePageChange = async (page: number) => {
    setClientPage(page);

    window.scrollTo({ top: 200, behavior: 'auto' });

    if (page > currentPage) {
      handleNext?.();
    } else {
      handlePrevious?.();
    }
  };

  useEffect(() => {
    if (paginationTotalRows) {
      setResetPaginationToggle(!resetPaginationToggle);
    }
  }, [paginationTotalRows]);

  const handleRemoveFilters = (filterId: string) => {
    onRemoveFilters?.(filterId);

    deleteSearchParams({
      searchParams,
      key: filterId,
      setSearchParams
    });
  };

  const handleRemoveAllFilters = () => {
    setShowAllFilters(false);
    onRemoveAllFilters?.();

    if (filters?.length) {
      deleteBulkSearchParams({
        searchParams,
        keys: filters.map((filter) => filter.filterId),
        setSearchParams
      });
    }
  };

  return (
    <Box>
      {filters && filters.length > 0 && (
        <Box sx={filtersContainer} data-table-filters>
          {(showAllFilters
            ? filters
            : filters.slice(0, MAX_VISIBLE_FILTERS)
          ).map((filter) => (
            <Box key={filter.filterId}>
              <Chip
                variant="outlined"
                label={filter.label}
                onDelete={() => handleRemoveFilters?.(filter.filterId)}
              />
            </Box>
          ))}
          {filters.length > MAX_VISIBLE_FILTERS && (
            <Link
              onClick={() => setShowAllFilters((previous) => !previous)}
              component="button"
            >
              {showAllFilters
                ? 'Show fewer'
                : `+${filters.length - MAX_VISIBLE_FILTERS} more`}
            </Link>
          )}
          <Link onClick={handleRemoveAllFilters} component="button">
            Clear all filters
          </Link>
        </Box>
      )}
      <DataTable
        selectableRowsComponent={CustomCheckbox as any}
        selectableRowsComponentProps={{ selectableRowsSingle }}
        customStyles={getCustomStyles(
          palette,
          props.headStyles,
          props.sortField,
          props.alwaysShowScrollbar,
          props.minTableWidth
        )}
        selectableRowDisabled={selectableRowDisabled}
        sortIcon={<SwapVertIcon />}
        progressPending={isLoading}
        progressComponent={
          <Box sx={inbetweenStateContainer}>
            <Loading />
            <Typography
              variant="body2"
              sx={{
                ...inbetweenStateTitle,
                color: palette.primary.main
              }}
            >
              Fetching data
            </Typography>
          </Box>
        }
        noDataComponent={
          <Box
            sx={{
              ...inbetweenStateContainer,
              backgroundColor: isFiltering
                ? palette.warning.main
                : palette.customGrey.light,
              gap: !emptyStateComponent ? 3 : 1
            }}
          >
            <Box
              sx={{
                ...noDataIconContainer,
                backgroundColor: isFiltering
                  ? palette.warning.light
                  : palette.customGrey.medium
              }}
            >
              {isFiltering && (
                <InfoOutlinedIcon sx={{ color: palette.warning.dark }} />
              )}
              {!isFiltering && <DoNotDisturbIcon color="primary" />}
            </Box>
            {emptyStateComponent ? (
              emptyStateComponent
            ) : (
              <>
                <Typography
                  variant="body2"
                  sx={{
                    ...inbetweenStateTitle,
                    color: isFiltering
                      ? palette.warning.dark
                      : palette.text.primary
                  }}
                >
                  Nothing {isFiltering ? 'found' : 'to show'}
                </Typography>
                {isFiltering && (
                  <Typography variant="body2" sx={inbetweenStateSubtitle}>
                    Search for something else or adjust your filters
                  </Typography>
                )}
              </>
            )}
          </Box>
        }
        persistTableHead
        onChangePage={handlePageChange}
        paginationResetDefaultPage={resetPaginationToggle}
        {...props}
        columns={columnsFormatted}
        paginationPerPage={props.paginationPerPage || 50}
        paginationComponent={(props: PaginationComponentProps) => (
          <Box sx={paginationContainer}>
            <TablePagination
              {...props}
              currentPage={serverPage ?? props.currentPage}
            />
          </Box>
        )}
        onChangeRowsPerPage={(currentRowsPerPage, currentPage) => {
          window.scrollTo({ top: 200, behavior: 'auto' });
          if (props?.onChangeRowsPerPage) {
            props.onChangeRowsPerPage(currentRowsPerPage, currentPage);
          }
        }}
      />
    </Box>
  );
}

export { TableV2 };
export type { TableV2Column };
